import type { ReactNode } from 'react';

import type { AccessGroup } from '@/api/auth';
import NotFound from '@/pages/NotFound';

import { useSession } from './session';

interface Props {
  /** The groups §5 gives this screen. An empty intersection is a refusal, never a redirect. */
  groups: AccessGroup[];
  children: ReactNode;
}

/**
 * The group half of the guard: `RequireSession` answers *is there a session*, this answers *may
 * this session be here at all*.
 *
 * ⚠️ **§5's table was enforced in the sidebar only.** An item a group may not use is not rendered
 * (`visibleNavItems`), which is the right answer for navigation and no answer at all for an address
 * typed into the bar: a Recruiter or an Interviewer reaching `/questions` or `/settings` was served
 * the screen. It is harmless while those addresses hold a placeholder and stops being harmless in
 * the sprint that builds Settings, which would inherit a route anyone can reach — so the gate is
 * moved to the router now, from the same `NAV_ITEMS` table the sidebar reads, rather than after.
 *
 * ⚠️ **The refusal renders `NotFound`, not §8's Forbidden state, and that is deliberate — do not
 * "improve" it back.** It shipped as *"You do not have access to this screen."* for one review
 * cycle, and that sentence hands out exactly what §5 spends its whole section withholding:
 * `navItems.ts` does not render an item a group may not use because *"a disabled item still tells
 * the viewer the screen exists"*, and §5 closes by naming the outcome — *"an interviewer who can
 * see that a Vacancies screen exists will ask to be let into it."* A Forbidden state on
 * `/settings` is that same disclosure arriving through the address bar.
 *
 * It also inverts the property the backend settled on purpose. **TASK-029 chose 404, not 403**, so
 * a refusal is indistinguishable from an absence and no state can ever be evidence that a record
 * exists; `ApiErrorState` implements that faithfully for answers the API actually gave. This gate
 * decides on the client, from `NAV_ITEMS`, about a route the server never saw — so here the state
 * *is* the evidence, and the only answer that leaks nothing is the one the app already gives for
 * an address it has no screen for: *"There is nothing at this address."* Designed, inside the
 * shell, with a way back (§8), and identical to what `/nope` renders.
 *
 * The gate itself stays: a group still must not reach the screen. Only the wording of the refusal
 * changed.
 *
 * This is the client's copy of a rule the API also enforces; it decides what is *rendered*, never
 * what may be *read*. Every screen behind it still asks the API, which refuses on its own.
 */
export function RequireGroup({ groups, children }: Props) {
  const session = useSession();

  // `RequireSession` is upstream of every route this wraps and renders nothing of them until the
  // session has resolved, so `data` is here by now; the optional chain is the type, not a case.
  if (groups.some((group) => session.data?.groups.includes(group))) {
    return <>{children}</>;
  }

  return <NotFound />;
}
